import { multiplyQuaternion, normalizeQuaternion, rotateVectorByQuaternion, tiltOnlyQuaternion, type QuaternionWxyz, type Vector3 } from "./orientation";

// Display stability guard, not a clinical upright threshold. The legacy
// default guard (≈5.74° from vertical) stays available for explicit use.
export const POSITION_PROJECTION_THRESHOLD = 0.1;
// Default upright/inverted band: crown axis within 45° of vertical.
export const DEFAULT_VERTICAL_POSITION_THRESHOLD_DEGREES = 45;
export const DEFAULT_POSITION_PROJECTION_THRESHOLD = Math.sin(DEFAULT_VERTICAL_POSITION_THRESHOLD_DEGREES * Math.PI / 180);

/** sin(inclination) guard for a configured upright/inverted band in degrees, clamped to 0–90. */
export function projectionThresholdForDegrees(degrees?: number | null): number {
  const value = typeof degrees === "number" && Number.isFinite(degrees) ? degrees : DEFAULT_VERTICAL_POSITION_THRESHOLD_DEGREES;
  return Math.sin(Math.max(0, Math.min(90, value)) * Math.PI / 180);
}
// sin(inclination): below this numerical guard the display heading is undefined.
// Retain the deterministic shortest tilt there; no previous pose or time state.
export const LONGITUDINAL_PROJECTION_THRESHOLD = 1e-6;
export const COMBINED_SCALE = 0.0035;
export const COMBINED_NECK: Record<"head" | "torso", Vector3> = {
  head: [0, -160, -56], torso: [-7, 180, -120],
};
export const DEFAULT_COMBINED_CAMERA = { position: [3.5, 3.6, 4.5], target: [0, -0.15, 0.25] } as const;

/** Combined-only display gauge, in the calibrated frame before mesh alignment.
 * Crown +Y projects toward world -Z when defined. Left multiplication about
 * world up preserves body gravity and inclination, independently for each sensor.
 * At/below the guard retain shortest tilt (a heading jump is possible at its
 * boundary, especially near inversion). This does not estimate anatomical yaw.
 */
export function combinedDisplayQuaternion(model: QuaternionWxyz): QuaternionWxyz {
  const tilt = tiltOnlyQuaternion(model).quaternion;
  const crown = rotateVectorByQuaternion([0, 1, 0], tilt);
  if (Math.hypot(crown[0], crown[2]) <= LONGITUDINAL_PROJECTION_THRESHOLD) return tilt;
  const halfHeading = Math.atan2(crown[0], -crown[2]) / 2;
  return normalizeQuaternion(multiplyQuaternion([Math.cos(halfHeading), 0, Math.sin(halfHeading), 0], tilt))!;
}

export function positionAngle(model: QuaternionWxyz, projectionThreshold = DEFAULT_POSITION_PROJECTION_THRESHOLD): number | null {
  const q = normalizeQuaternion(model);
  if (!q) return null;
  const up = rotateVectorByQuaternion([0, 1, 0], [q[0], -q[1], -q[2], -q[3]]);
  if (Math.hypot(up[0], up[2]) < projectionThreshold) return null;
  return (Math.atan2(up[0], up[2]) * 180 / Math.PI + 360) % 360;
}

/** Same displayed calibrated quaternion and strict projection guard as the dial.
 * +Y is the sensor's longitudinal/crown axis. Missing/invalid is never vertical.
 */
export function verticalPosition(model: QuaternionWxyz, projectionThreshold = DEFAULT_POSITION_PROJECTION_THRESHOLD): "upright" | "inverted" | null {
  const q = normalizeQuaternion(model);
  if (!q) return null;
  const up = rotateVectorByQuaternion([0, 1, 0], [q[0], -q[1], -q[2], -q[3]]);
  return Math.hypot(up[0], up[2]) < projectionThreshold ? up[1] > 0 ? "upright" : "inverted" : null;
}

// Alignment and landmark are in asset units. Rotation is the independent
// calibrated common-heading pose; the common world anchor is the origin.
export function combinedVertex(vertex: Vector3, alignedNeck: Vector3, alignment: QuaternionWxyz, model: QuaternionWxyz): Vector3 {
  const aligned = rotateVectorByQuaternion(vertex, alignment);
  const local = aligned.map((v, i) => (v - alignedNeck[i]) * COMBINED_SCALE) as unknown as Vector3;
  return rotateVectorByQuaternion(local, combinedDisplayQuaternion(model));
}
